import React from "react";
import { motion } from "framer-motion";
import Section from "../ui/Section";
import Card from "../ui/Card";
import { Code, Database, Server, Cpu, User, ServerCog } from "lucide-react";

const About: React.FC = () => {
  const personalInfo = [
    {
      icon: <User size={24} />,
      title: "Profilo",
      content:
        "Sviluppatore software con background elettronico (Perito 1992). Oltre 20 anni di esperienza tra sviluppo, sistemi e integrazione hardware/software in ambito industriale.",
    },
    {
      icon: <Code size={24} />,
      title: "Approccio",
      content:
        "Pragmatico e orientato al risultato. Abituato a lavorare su sistemi esistenti complessi (anche legacy), migliorandoli senza fermare la produzione.",
    },
    {
      icon: <Database size={24} />,
      title: "Tecnologie",
      content:
        "C#, VB.NET, JavaScript, PHP, Python. Esperienza con .NET Framework (anche legacy), MySQL, Node.js e sviluppo full-stack quando serve.",
    },
    {
      icon: <Server size={24} />,
      title: "Sistemi",
      content:
        "Windows Server (2012–2022) e Linux (Ubuntu, CentOS). Configurazione servizi, sicurezza, gestione reti e ambienti di produzione.",
    },
    {
      icon: <ServerCog size={29} />,
      title: "Virtualizzazione",
      content:
        "VMware vSphere, Hyper-V, Docker. Gestione infrastrutture virtualizzate e ambienti containerizzati.",
    },
    {
      icon: <Cpu size={24} />,
      title: "Industriale",
      content:
        "Sviluppo software per macchine di misura e automazione industriale. Integrazione PLC Siemens, comunicazioni seriali e gestione dispositivi hardware.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Section
      id="about"
      title="Chi Sono"
      subtitle="Sviluppo soluzioni che collegano macchine industriali, software e dati."
      className="bg-gray-50 dark:bg-gray-900"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mx-auto text-left mb-12 space-y-5">
            <p className="text-gray-700 dark:text-gray-300">
              Sono uno sviluppatore specializzato nell’
              <span className="font-semibold">
                integrazione tra macchine industriali e software
              </span>
              .
            </p>

            <p className="text-gray-700 dark:text-gray-300">
              Aiuto le aziende a collegare strumenti, impianti e sistemi
              informatici, evitando perdite di tempo tra comunicazioni instabili
              e software difficili da gestire.
            </p>

            <p className="text-gray-700 dark:text-gray-300">
              Il mio vantaggio è conoscere entrambi i mondi:
              <span className="font-semibold"> hardware reale </span>
              (seriali, protocolli, strumenti) e
              <span className="font-semibold"> software moderno </span>
              (.NET, web, database).
            </p>

            <p className="text-gray-900 dark:text-gray-100 font-medium">
              Risultato: sistemi che funzionano davvero, non solo sulla carta.
            </p>
          </div>
          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
            La mia storia
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Lavoro tra software e macchine industriali da oltre 20 anni. Mi
            occupo di integrazione tra hardware, applicazioni e sistemi web,
            trasformando impianti esistenti in soluzioni moderne e monitorabili.
          </p>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Ho iniziato programmando a basso livello (assembly, sistemi storici
            come Commodore e CP/M), costruendo una conoscenza solida di come
            funzionano davvero hardware e software. Questo oggi mi permette di
            risolvere problemi dove altri si fermano.
          </p>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            In ambito industriale ho sviluppato software per automazione,
            raccolta dati e gestione produzione, lavorando a lungo in Candiani
            Spa su sistemi per magazzini e processi produttivi.
          </p>

          <p className="text-gray-700 dark:text-gray-300">
            Oggi sviluppo applicazioni .NET e interfacce web (React) per il
            testing e la gestione dati di strumenti industriali, collegando
            macchine, database e dashboard in un unico flusso.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mt-4 font-medium">
            Il mio punto di forza? Far parlare tra loro sistemi che normalmente
            non si capiscono.
          </p>
          {/* Timeline overlay */}
          <div className="relative -bottom-6 left-0 right-0 flex justify-center">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <span className="block text-3xl font-bold text-primary-600 dark:text-primary-400">
                    30+
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Anni di esperienza
                  </span>
                </div>
                <div className="h-10 w-px bg-gray-300 dark:bg-gray-700"></div>
                <div className="text-center">
                  <span className="block text-3xl font-bold text-secondary-600 dark:text-secondary-400">
                    100+
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Progetti completati
                  </span>
                </div>
                <div className="h-10 w-px bg-gray-300 dark:bg-gray-700"></div>
                <div className="text-center">
                  <span className="block text-3xl font-bold text-accent-600 dark:text-accent-400">
                    5
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Tecnologie principali
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden">
            <img
              src="/images/evoluzione-tecnologica.jpg"
              alt="La mia evoluzione tecnologica"
              loading="lazy"
              decoding="async"
              className="object-cover w-full h-full rounded-lg"
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16"
      >
        {personalInfo.map((item, index) => (
          <motion.div key={index} variants={itemVariants}>
            <Card className="h-full">
              <div className="flex items-start">
                <div className="mr-4 p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.content}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
};

export default About;
